<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText, SatElasticPanel } from "@chatfood/design-system";
import { LoyaltyBalanceButton } from "../loyalty-balance-button";
import { LoyaltyListing } from "../loyalty-listing";
import { LoyaltyProgression } from "../loyalty-progression";
import { useLocale } from "~/composables/use-locale";
import { useCustomerStore } from "~/stores/customer";

const css = bemBuilder("loyalty-reward");

const customer = useCustomerStore();

const { t } = useLocale();

type Views = "rewards" | "progression";

const views = computed<Views[]>(() => {
  const baseViews: Views[] = ["rewards"];

  if (customer.loyalty.nextTier) {
    return [...baseViews, "progression"];
  }

  return baseViews;
});

const viewState = ref<Views>("rewards");

const showModal = ref(false);
const hideDetails = ref(false);

const onDisplayModal = () => {
  showModal.value = !showModal.value;
};

const setViewState = (state: Views) => {
  viewState.value = state;
};

const viewLabel = {
  rewards: t("components.loyalty_rewards.reward_tab"),
  progression: t("components.loyalty_rewards.progression_tab"),
};

watch(
  () => showModal.value,
  (val) => {
    if (val) hideDetails.value = false;
  },
);
</script>

<template>
  <div :class="css('')">
    <LoyaltyBalanceButton @click="onDisplayModal" />
    <SatElasticPanel
      v-model="showModal"
      show-close
      padding="none"
      width-fit-content
    >
      <div :class="css('wrapper')">
        <div v-if="!hideDetails" data-test="header">
          <AtomText :class="css('title')" color="color-text-body" type="h3">
            {{ t("components.loyalty_rewards.title") }}
          </AtomText>
          <ul :class="css('tab')">
            <li v-for="view in views" :key="view">
              <a
                :class="[
                  css('tab-link'),
                  { [css('tab-link', 'active')]: viewState === view },
                ]"
                @click.prevent="setViewState(view)"
              >
                <AtomText color="color-text-secondary">
                  {{ viewLabel[view] }}
                </AtomText>
              </a>
            </li>
          </ul>
        </div>
        <LoyaltyListing
          v-if="viewState === 'rewards'"
          @dismiss-modal="showModal = false"
          @selected="(state) => (hideDetails = state)"
        />
        <LoyaltyProgression v-if="viewState === 'progression'" />
      </div>
    </SatElasticPanel>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-reward {
  @media (width > $breakpoint-sm) {
    &__wrapper {
      width: 400px;
      min-height: 400px;
    }
  }

  &__title {
    padding: var(--spacing-md);
  }

  &__tab {
    width: 100%;
    margin: 0;
    display: flex;
    list-style: none;
    gap: var(--spacing-sm);
    padding-inline: var(--spacing-md);
    border-bottom: solid 1px var(--color-base-95);

    &-link {
      display: flex;
      cursor: pointer;
      border-bottom: solid 3px transparent;
      padding: var(--spacing-sm) var(--spacing-md);
      transition: 0.3s ease;

      &--active {
        border-color: var(--color-primary);
      }
    }
  }
}
</style>
